import type {
  ExtinctionLawName,
  Manifest,
  SpectraPackage,
} from "./types";

/** Default object id when a caller does not specify one. */
export const DEFAULT_OBJECT_ID = "j1628";

/**
 * Objects with a built data package under `web/public/data/<id>/v1/`.
 * Adding a new quasar means building its package with
 * `clq-build --object <id>` and adding a row here.
 */
export const AVAILABLE_OBJECTS: Array<{ id: string; label: string }> = [
  { id: "j1628", label: "J1628+4329 (z = 0.26)" },
  { id: "j0159", label: "J0159+0033 (z = 0.31)" },
  { id: "j0950", label: "J0950+5128 (z = 0.21)" },
];

function packageBase(objectId: string): string {
  const baseUrl = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
  const trimmed = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return `${trimmed}/data/${objectId}/v1`;
}

async function fetchFloat32(url: string, expectedLength: number): Promise<Float32Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  const buffer = await response.arrayBuffer();
  const array = new Float32Array(buffer);
  if (array.length !== expectedLength) {
    throw new Error(
      `Unexpected array length for ${url}: got ${array.length}, expected ${expectedLength}`,
    );
  }
  return array;
}

function buildWavelengthGrid(start: number, step: number, n: number): Float32Array {
  const grid = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    grid[i] = start + i * step;
  }
  return grid;
}

/**
 * Fetch a data package from `/data/<objectId>/v1/` and return parsed arrays.
 * The function performs no transformation beyond binary decoding so the
 * caller works directly with the on-disk units defined in the data contract.
 */
export async function loadPackage(
  objectId: string = DEFAULT_OBJECT_ID,
): Promise<SpectraPackage> {
  const base = packageBase(objectId);
  const manifest = (await (await fetch(`${base}/manifest.json`)).json()) as Manifest;
  const [nEpoch, nLambda] = manifest.spectra.shape;
  const totalFloats = nEpoch * nLambda;

  const [flux, uncertainty] = await Promise.all([
    fetchFloat32(`${base}/${manifest.spectra.file}`, totalFloats),
    fetchFloat32(`${base}/${manifest.uncertainty.file}`, totalFloats),
  ]);

  const extinctionEntries = await Promise.all(
    manifest.extinction.laws.map(async (law: ExtinctionLawName) => {
      const data = await fetchFloat32(
        `${base}/${manifest.extinction.files[law]}`,
        nLambda,
      );
      return [law, data] as const;
    }),
  );

  const extinction: Partial<Record<ExtinctionLawName, Float32Array>> = {};
  for (const [law, data] of extinctionEntries) {
    extinction[law] = data;
  }

  return {
    manifest,
    wavelength: buildWavelengthGrid(
      manifest.wavelength_rest.start_A,
      manifest.wavelength_rest.step_A,
      manifest.wavelength_rest.n,
    ),
    flux,
    uncertainty,
    extinction: extinction as Record<ExtinctionLawName, Float32Array>,
  };
}

/**
 * Return a view of the spectrum at a given epoch index without copying.
 * Callers must not mutate the returned subarray; React state objects own
 * the underlying buffer.
 */
export function spectrumAt(pkg: SpectraPackage, epochIndex: number): Float32Array {
  const { n } = pkg.manifest.wavelength_rest;
  const start = epochIndex * n;
  return pkg.flux.subarray(start, start + n);
}

export function uncertaintyAt(pkg: SpectraPackage, epochIndex: number): Float32Array {
  const { n } = pkg.manifest.wavelength_rest;
  const start = epochIndex * n;
  return pkg.uncertainty.subarray(start, start + n);
}
